import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_URL } from "../../config/constants";
import { MOOD_ICONS } from "../../config/moodIcons";
import AppSidebar from "../../components/AppSidebar";
import TopBar from "../../components/TopBar";
import WeeklyFormSummaryModal from "../../components/weeklyFormSummaryModal";
import {
  Layout, Main, PageTitle, WeeksGrid, WeekCard, EmptyCard, WeekTitle,
  StatList, MoodRow, MoodIcon, InProgressText, CardActions, CardButton, EmptyState,
  Panel, PanelHeader, PanelTitle, RangeTabs, RangeTab,
  BarList, BarRow, BarLabelRow, BarValue, BarTrack, BarFill,
} from "./styles";

const CARDS_PER_ROW = 5;
const RANGES = ["current_week", "last_week", "last_month", "all"];

// 1 h 08 min / 44 min. Los minutos van con dos dígitos solo si hay horas.
const fmtActiveTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (!hours) return `${minutes} min`;
  return `${hours} h ${String(minutes % 60).padStart(2, "0")} min`;
};

const ActivityBar = ({ label, opened, prescribed, strong }) => (
  <BarRow>
    <BarLabelRow>
      <span>{label}</span>
      <BarValue>{opened} / {prescribed}</BarValue>
    </BarLabelRow>
    <BarTrack>
      <BarFill $pct={prescribed ? Math.round((opened / prescribed) * 100) : 0} $strong={strong} />
    </BarTrack>
  </BarRow>
);

const PatientSummary = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const [patient, setPatient] = useState(location.state?.patient || null);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // semana cuyo formulario se está viendo
  const [range, setRange] = useState("all");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };

    if (!location.state?.patient) {
      fetch(`${API_URL}/patients/${id}`, { headers })
        .then((r) => r.json())
        .then((data) => { if (!data.error) setPatient(data); })
        .catch(() => {});
    }

    fetch(`${API_URL}/patients/${id}/summary`, { headers })
      .then((r) => r.json())
      .then((data) => setWeeks(Array.isArray(data.weeks) ? data.weeks : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Las barras se piden aparte: cambiar de periodo no recarga las tarjetas.
  useEffect(() => {
    fetch(`${API_URL}/patients/${id}/activities-opened?range=${range}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data.categories) ? data.categories : []))
      .catch(() => {});
  }, [id, range]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cada actividad pertenece a una sola categoría, así que el total es la suma.
  const total = categories.reduce(
    (acc, c) => ({ opened: acc.opened + c.opened, prescribed: acc.prescribed + c.prescribed }),
    { opened: 0, prescribed: 0 }
  );

  // Huecos para completar la última fila, como en el diseño.
  const fillers = weeks.length % CARDS_PER_ROW
    ? CARDS_PER_ROW - (weeks.length % CARDS_PER_ROW)
    : 0;

  return (
    <Layout>
      <TopBar />
      <AppSidebar patient={patient} activeNav="summary" />
      <Main>
        <PageTitle>{t("summary.title")}</PageTitle>

        {loading ? (
          <EmptyState>{t("common.loading")}</EmptyState>
        ) : weeks.length === 0 ? (
          <EmptyState>{t("summary.noTreatmentStart")}</EmptyState>
        ) : (
          <WeeksGrid>
            {weeks.map((week) => (
              <WeekCard key={week.week_number}>
                <WeekTitle>{t("summary.week", { number: week.week_number })}</WeekTitle>

                {week.in_progress ? (
                  <InProgressText>{t("summary.inProgress")}</InProgressText>
                ) : (
                  <>
                    <StatList>
                      <span>
                        {t("summary.openedActivities", {
                          opened: week.opened_activities,
                          total: week.prescribed_activities,
                        })}
                      </span>
                      <span>{t("summary.activeTime", { time: fmtActiveTime(week.active_seconds) })}</span>
                      <span>{t("summary.activeDays", { days: week.active_days })}</span>
                      {week.form ? (
                        <MoodRow>
                          <MoodIcon src={MOOD_ICONS[week.form.mood]} alt="" />
                          {t(`weeklyForm.options.mood.${week.form.mood}`)}
                        </MoodRow>
                      ) : (
                        <span>-</span>
                      )}
                    </StatList>

                    <CardActions>
                      <CardButton type="button">{t("summary.seeDetails")}</CardButton>
                      <CardButton
                        type="button"
                        disabled={!week.form}
                        onClick={() => setOpenForm(week)}
                      >
                        {week.form ? t("summary.seeForm") : t("summary.noForm")}
                      </CardButton>
                    </CardActions>
                  </>
                )}
              </WeekCard>
            ))}

            {Array.from({ length: fillers }, (_, i) => <EmptyCard key={`filler-${i}`} />)}
          </WeeksGrid>
        )}

        {!loading && (
          <Panel>
            <PanelHeader>
              <PanelTitle>{t("summary.activitiesOpened")}</PanelTitle>
              <RangeTabs>
                {RANGES.map((option) => (
                  <RangeTab
                    key={option}
                    type="button"
                    $active={range === option}
                    onClick={() => setRange(option)}
                  >
                    {t(`summary.ranges.${option}`)}
                  </RangeTab>
                ))}
              </RangeTabs>
            </PanelHeader>

            <BarList>
              <ActivityBar
                label={t("summary.allCategories")}
                opened={total.opened}
                prescribed={total.prescribed}
                strong
              />
              {categories.map((category) => (
                <ActivityBar
                  key={category.activity_type_id}
                  label={t(`activityTypes.${category.activity_type_id}.name`, { defaultValue: category.name })}
                  opened={category.opened}
                  prescribed={category.prescribed}
                />
              ))}
            </BarList>
          </Panel>
        )}
      </Main>

      {openForm && (
        <WeeklyFormSummaryModal
          weekNumber={openForm.week_number}
          form={openForm.form}
          onClose={() => setOpenForm(null)}
        />
      )}
    </Layout>
  );
};

export default PatientSummary;
